import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from './Button';

interface PaginationProps {
  /** 1-based current page */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** When omitted the page-size selector is hidden. */
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: readonly number[];
  /** Show numbered page buttons (desktop); prev/next only when false. */
  showPageNumbers?: boolean;
  /** Disable all controls, e.g. while a fetch is in flight. */
  disabled?: boolean;
  className?: string;
}

const DEFAULT_SIZES: readonly number[] = [25, 50, 100];

/**
 * Compact window of page numbers: first, last, current ±1, with `…` gaps.
 */
function pageWindow(page: number, pageCount: number): Array<number | 'gap'> {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const pages = new Set<number>([1, pageCount, page - 1, page, page + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= pageCount).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('gap');
    out.push(sorted[i]);
  }
  return out;
}

export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_SIZES,
  showPageNumbers = true,
  disabled = false,
  className,
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const to = Math.min(current * pageSize, total);

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex flex-wrap items-center justify-between gap-3', className)}
    >
      <div className="flex items-center gap-3">
        <span className="text-sm text-text-secondary whitespace-nowrap">
          Showing {from}–{to} of {total}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-sm text-text-secondary">
            <span className="hidden sm:inline">Per page</span>
            <select
              aria-label="Rows per page"
              value={pageSize}
              disabled={disabled}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="bg-bg-tertiary border border-border-default rounded-lg text-sm text-text-primary px-2 py-1 focus:outline-none focus:ring-2 focus:ring-brand-blue disabled:opacity-50"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          aria-label="Previous page"
          disabled={disabled || current <= 1}
          onClick={() => onPageChange(current - 1)}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Prev</span>
        </Button>

        {showPageNumbers ? (
          pageWindow(current, pageCount).map((item, idx) =>
            item === 'gap' ? (
              <span key={`gap-${idx}`} className="px-1 text-text-muted" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={item}
                type="button"
                disabled={disabled}
                aria-current={item === current ? 'page' : undefined}
                onClick={() => onPageChange(item)}
                className={cn(
                  'min-w-9 h-9 px-2 rounded-lg text-sm font-medium border transition-colors',
                  item === current
                    ? 'bg-brand-blue text-white border-brand-blue'
                    : 'text-text-secondary border-border-default hover:bg-bg-tertiary',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {item}
              </button>
            )
          )
        ) : (
          <span className="px-2 text-sm text-text-secondary whitespace-nowrap">
            Page {current} of {pageCount}
          </span>
        )}

        <Button
          variant="ghost"
          size="sm"
          aria-label="Next page"
          disabled={disabled || current >= pageCount}
          onClick={() => onPageChange(current + 1)}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </nav>
  );
}
